import React from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Button, { ILink } from 'tbk-components/src/components/Button';
import Image, { IImage } from 'tbk-components/src/components/Image';
import AnimatedArcs from '../AnimatedArcs';
import FormLightboxButton, {
	IFormLightboxButtonProps,
} from '../FormLightboxButton';

export interface INextStepsTiles {
	/**
	 * Heading
	 */
	heading?: string;
	/**
	 * Description
	 */
	description?: string;
	/**
	 * CTA
	 */
	cta?: ILink;
	lightboxCTA?: IFormLightboxButtonProps;
}

export interface INextStepsProps extends IBaseElementProps {
	/**
	 * Background Image - suggested size 688px x 623px
	 */
	backgroundImage?: IImage;
	/**
	 * Heading
	 */
	heading?: string;
	/**
	 * Tiles
	 */
	tiles?: INextStepsTiles[];
	/**
	 * Enable Line Animations - only used in Vertical layout
	 */
	enableLineAnimations?: boolean;
	/**
	 * Line Animation Delay in Milliseconds - only used in Vertical layout
	 */
	lineAnimationDelay?: number;
	/**
	 * Line Animation Offset in Pixels - only used in Vertical layout
	 */
	lineAnimationOffset?: number;
	/**
	 * Will invert the button colors to secondary
	 */
	inverted?: boolean;
}

/**
 * Calian Next Steps
 * @icon welcome-widgets-menus
 * @block
 */
const NextSteps: React.FC<INextStepsProps> = ({
	id,
	className,
	classNames = [],
	backgroundImage,
	heading,
	tiles = [],
	enableLineAnimations = true,
	lineAnimationDelay = 250,
	lineAnimationOffset = 150,
	inverted = false,
}) => {
	let arcOneColor = 'var(--bs-white)';
	let arcTwoColor = 'var(--bs-primary)';

	return (
		<div
			id={id}
			className={className || cn('calian-next-steps', ...classNames)}
		>
			<div className="calian-next-steps__container container-fluid position-relative mw-1440px">
				<div className="calian-next-steps__wrapper container-fluid px-0 position-relative mw-1240px overflow-hidden d-flex align-items-center px-0">
					<div className="calian-next-steps__content position-relative w-100 overflow-hidden py-lg-10 rounded-5">
						<div className="calian-next-steps__content--background position-absolute top-0 bottom-0 left-0 right-0 w-100">
							<div className="calian-next-steps__image-overlay position-absolute top-0 bottom-0 start-0 end-0 z-1 w-75"></div>
							{backgroundImage && (
								<div className="calian-next-steps__image-wrapper position-absolute top-0 bottom-0 end-0">
									<Image
										className="calian-next-steps__image img-fluid h-100 object-fit-cover"
										{...backgroundImage}
									/>
								</div>
							)}
						</div>
						<div className="calian-next-steps__inner-content position-relative z-2 p-5 rounded-5">
							{heading && (
								<h2 className="ms-lg-4 ps-lg-4 ms-xl-10 mb-lg-9 ps-xl-1 text-white">
									{heading}
								</h2>
							)}
							{tiles && tiles.length > 0 && (
								<div className="container-fluid mx-lg-3 w-xl-85 ms-xl-6 me-xl-auto p-0 ps-lg-4">
									<div className="row d-flex gap-3">
										{tiles.map((tile, index) => (
											<div
												key={index}
												className="next-step position-relative p-0 d-flex"
											>
												<div className="min-height-lg-265px flex-grow-1 d-flex flex-column justify-content-between align-items-start p-4 py-lg-6 px-4 px-md-5 bg-white rounded-5">
													<div>
														{tile.heading && (
															<h4 className="mb-2">
																{tile.heading}
															</h4>
														)}
														<p className="mb-3">
															{tile.description}
														</p>
													</div>
													{tile.cta &&
														tile.cta.label && (
															<Button
																{...tile.cta}
																className={`btn btn-sm btn-${
																	inverted
																		? 'secondary'
																		: 'primary'
																}`}
																key={index}
															/>
														)}
													{tile?.lightboxCTA
														?.formLightboxButton
														.label?.length > 0 && (
														<FormLightboxButton
															{...tile.lightboxCTA}
														/>
													)}
												</div>
											</div>
										))}
									</div>
								</div>
							)}
						</div>
						{enableLineAnimations && (
							<AnimatedArcs
								arcsStyle={'Component - Two Lines'}
								arcsPosition="Left"
								arcOneColor={arcOneColor}
								arcOneStrokeWidth={4}
								arcTwoColor={arcTwoColor}
								arcTwoStrokeWidth={3}
								animationDelay={lineAnimationDelay}
								animationOffset={lineAnimationOffset}
							/>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default NextSteps;
